import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ compact = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);

  // Render reCAPTCHA when component mounts
  useEffect(() => {
    const renderRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current && recaptchaWidgetId.current === null) {
        try {
          recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
          });
        } catch (e) {
          // Already rendered or error - silently handle
        }
      }
    };

    // Check if grecaptcha is ready
    if (window.grecaptcha && window.grecaptcha.render) {
      renderRecaptcha();
    } else {
      // Wait for grecaptcha to load
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkInterval);
          renderRecaptcha();
        }
      }, 100);

      // Cleanup
      return () => clearInterval(checkInterval);
    }
  }, [RECAPTCHA_SITE_KEY]); // Added dependency

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token using widget ID
      let recaptchaToken = '';
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        try {
          recaptchaToken = window.grecaptcha.getResponse(recaptchaWidgetId.current);
        } catch (err) {
          // Silently handle reCAPTCHA error
        }
      } else if (window.grecaptcha) {
        try {
          recaptchaToken = window.grecaptcha.getResponse();
        } catch (err) {
          // Silently handle reCAPTCHA error
        }
      }
      
      if (!recaptchaToken) {
        toast.error('Please complete the reCAPTCHA verification');
        setIsSubmitting(false);
        return;
      }

      // Submit form to backend
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        // Reset reCAPTCHA
        if (window.grecaptcha && recaptchaWidgetId.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        } else if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      // Error is already shown to user via toast
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className={compact ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
        <div className="space-y-2">
          <Label htmlFor="name" className="font-montserrat font-medium">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            className="font-montserrat"
            placeholder="John Doe"
            data-testid="contact-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="font-montserrat font-medium">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="font-montserrat"
            placeholder="john@example.com"
            data-testid="contact-email-input"
          />
        </div>

        {!compact && (
          <>
            <div className="space-y-2">
              <Label htmlFor="phone" className="font-montserrat font-medium">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="font-montserrat"
                placeholder="(941) 555-0123"
                data-testid="contact-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="font-montserrat font-medium">
                Subject *
              </Label>
              <Input
                id="subject"
                name="subject"
                type="text"
                required
                value={formData.subject}
                onChange={handleChange}
                className="font-montserrat"
                placeholder="How can we help?"
                data-testid="contact-subject-input"
              />
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="font-montserrat font-medium">
          Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          required
          value={formData.message}
          onChange={handleChange}
          className="font-montserrat min-h-[150px]"
          placeholder="Tell us about your financial goals..."
          data-testid="contact-message-input"
        />
      </div>

      {/* reCAPTCHA */}
      <div className="flex justify-center" data-testid="recaptcha-container">
        <div ref={recaptchaRef} className="g-recaptcha"></div>
      </div>

      <div className="text-sm text-gray-600 font-montserrat">
        <p>
          This site is protected by reCAPTCHA. By submitting this form, you agree to our{' '}
          <a href="/privacy" className="text-[#0a69b2] hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto bg-[#0a69b2] hover:bg-[#0858a0] text-white font-montserrat font-semibold px-8 py-3 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
        data-testid="contact-submit-button"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

export default ContactForm;
